import { useState } from 'react';
import { clsx } from 'clsx';
import { IconButton, Icon, Tooltip, Popover, icon } from '@nula/core-ui';
import styles from './icon-picker-button.module.css';

/**
 * Material Symbols names for the icons offered in the frame closed-state
 * picker. Curated for "what's in this group?" semantics — folder/inventory
 * for storage, groups/person for people, lightbulb/flag for ideas/goals,
 * task/event for to-dos, bookmark for refs, bug for issues, dashboard
 * for overviews. 12 fits a 4×3 grid without scrolling.
 */
export const FRAME_ICON_CHOICES = [
  'folder',
  'inventory_2',
  'category',
  'groups',
  'description',
  'dashboard',
  'lightbulb',
  'task',
  'flag',
  'bookmark',
  'bug_report',
  'event',
] as const;

export interface IconPickerButtonProps {
  /** Current icon name (Material Symbols). */
  value: string;
  onChange: (next: string) => void;
  /** Tooltip label on the trigger. Defaults to 'Frame icon'. */
  label?: string;
  /** Override the offered choices. Defaults to FRAME_ICON_CHOICES. */
  choices?: readonly string[];
}

/**
 * Trigger button + popover for picking a Material Symbols icon. Renders the
 * current selection as the trigger glyph; popover shows the choice grid.
 * Used by FrameFormattingToolbar — kept generic so other surfaces can
 * reuse if they need a similar picker.
 */
export function IconPickerButton({
  value,
  onChange,
  label = 'Frame icon',
  choices = FRAME_ICON_CHOICES,
}: IconPickerButtonProps) {
  const [open, setOpen] = useState(false);

  const content = (
    <div className={styles.popover} role="group" aria-label="Pick icon">
      {choices.map((name) => (
        <Tooltip key={name} title={name.replace(/_/g, ' ')} placement="top">
          <IconButton
            transparent
            aria-label={name}
            aria-pressed={value === name}
            className={clsx(styles.iconBtn, value === name && styles.iconBtnActive)}
            onClick={() => { onChange(name); setOpen(false); }}
          >
            <Icon name={name} size={icon.md} />
          </IconButton>
        </Tooltip>
      ))}
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="top" open={open} onOpenChange={setOpen}>
      <Tooltip title={label} placement="top" open={open ? false : undefined}>
        <IconButton
          transparent
          aria-label={label}
          aria-expanded={open}
          className={styles.btn}
        >
          <Icon name={value} size={icon.md} />
        </IconButton>
      </Tooltip>
    </Popover>
  );
}
